import React, { useEffect, useRef, useState } from "react";
import { Inbox, Phone, Users } from "lucide-react";
import PalcoEscalavel from "./multichannel/PalcoEscalavel";
import InboxMockup, { MOCKUP_LARGURA, MOCKUP_ALTURA } from "./multichannel/InboxMockup";
import CelularAtendimento, { CELULAR_LARGURA, CELULAR_ALTURA } from "./multichannel/CelularAtendimento";
import { NoCanal, Conexoes } from "./multichannel/CanaisOrbita";

/**
 * SecaoMultichannel — apresentacao do atendimento unificado.
 * O mockup reproduz a tela real /novo-chat/chat do sistema (sidebar, lista,
 * chat e painel do cliente) com dados ficticios. Canais reais orbitam o
 * produto e se conectam a ele por linhas que compartilham o sistema de
 * coordenadas do palco escalavel.
 */

// ---- Palco desktop/tablet (coordenadas de design) ----
// Margem lateral curta: o mockup ocupa ~78% da largura do palco e os canais
// ficam colados nas bordas dele.
const PALCO_LARGURA = 1640;
const MOCKUP_X = (PALCO_LARGURA - MOCKUP_LARGURA) / 2; // 180
const MOCKUP_Y = 24;
const PALCO_ALTURA = MOCKUP_Y + MOCKUP_ALTURA + 28;
const BORDA_ESQ = MOCKUP_X;
const BORDA_DIR = MOCKUP_X + MOCKUP_LARGURA;
const TAMANHO_NO = 96;
const TAMANHO_NO_VOIP = 84;
// Recuo lateral do mockup dentro do palco — os cards usam o mesmo recuo
// pra ficarem exatamente da largura do mockup.
const RECUO_LATERAL = `${((MOCKUP_X / PALCO_LARGURA) * 100).toFixed(3)}%`;

const NOS_DESKTOP = [
  { canal: "whatsapp", x: 68, y: 170, ate: [BORDA_ESQ, 240] },
  { canal: "instagram", x: 56, y: 395, ate: [BORDA_ESQ, 395] },
  { canal: "facebook", x: 68, y: 620, ate: [BORDA_ESQ, 550] },
  { canal: "telegram", x: PALCO_LARGURA - 68, y: 170, ate: [BORDA_DIR, 240] },
  { canal: "webchat", x: PALCO_LARGURA - 56, y: 395, ate: [BORDA_DIR, 395] },
  { canal: "voip", x: PALCO_LARGURA - 68, y: 620, ate: [BORDA_DIR, 550], tamanho: TAMANHO_NO_VOIP },
];

const LINHAS_DESKTOP = NOS_DESKTOP.map((n) => {
  const saida = (n.tamanho || TAMANHO_NO) / 2 + 8;
  return { de: [n.x < BORDA_ESQ ? n.x + saida : n.x - saida, n.y], ate: n.ate };
});

// ---- Palco tablet: canais em linha acima, mockup com largura total ----
const TABLET_LARGURA = MOCKUP_LARGURA;
const TABLET_MOCKUP_Y = 150;
const TABLET_ALTURA = TABLET_MOCKUP_Y + MOCKUP_ALTURA + 10;
const TABLET_NO_Y = 36;
const TAMANHO_NO_TABLET = 56;

const NOS_TABLET = ["whatsapp", "instagram", "facebook", "telegram", "webchat", "voip"].map((canal, i) => ({
  canal,
  x: TABLET_LARGURA / 2 + (i - 2.5) * 110,
  y: TABLET_NO_Y,
}));

const LINHAS_TABLET = NOS_TABLET.map((n, i) => ({
  de: [n.x, TABLET_NO_Y + TAMANHO_NO_TABLET / 2 + (n.canal === "voip" ? 26 : 10)],
  ate: [TABLET_LARGURA / 2 + (i - 2.5) * 70, TABLET_MOCKUP_Y],
}));

// ---- Palco mobile ----
const MOBILE_LARGURA = 360;
const CELULAR_X = (MOBILE_LARGURA - CELULAR_LARGURA) / 2; // 40
const CELULAR_Y = 130;
const MOBILE_ALTURA = CELULAR_Y + CELULAR_ALTURA + 10;
const TAMANHO_NO_MOBILE = 40;

const NOS_MOBILE = ["whatsapp", "instagram", "facebook", "telegram", "webchat", "voip"].map((canal, i) => ({
  canal,
  x: 30 + i * 60,
  y: 28,
}));

const LINHAS_MOBILE = NOS_MOBILE.map((n, i) => ({
  de: [n.x, n.canal === "voip" ? 72 : 60],
  ate: [100 + i * 32, CELULAR_Y],
}));

const FEATURES = [
  {
    icon: Inbox,
    titulo: "Caixa de entrada unificada",
    descricao: "Todas as conversas em uma fila com filtros por canal, tag e status.",
  },
  {
    icon: Phone,
    titulo: "VoIP integrado",
    descricao: "Ligações direto do painel, sem app extra. Histórico junto com o chat.",
  },
  {
    icon: Users,
    titulo: "Atendimento em equipe",
    descricao: "Distribua conversas por setor, transfira em 1 clique, veja quem atende o quê.",
  },
];

// Animacoes locais da secao (prefixo mc-), todas desligadas com reduced-motion
// e pausadas quando a secao sai da tela.
const CSS_SECAO = `
.mc-entrada { opacity: 0; transform: translateY(28px); transition: opacity 1s cubic-bezier(.22,1,.36,1), transform 1s cubic-bezier(.22,1,.36,1); }
.mc-entrada[data-entrou="true"] { opacity: 1; transform: none; }
@keyframes mc-flutuar { 0%, 100% { transform: translateY(0); } 50% { transform: translateY(-4px); } }
.mc-flutuar { animation: mc-flutuar 7s ease-in-out infinite; }
@keyframes mc-fluxo { to { stroke-dashoffset: -20; } }
.mc-fluxo { animation: mc-fluxo 2.4s linear infinite; }
@keyframes mc-respirar { 0%, 100% { opacity: .7; } 50% { opacity: 1; } }
.mc-respirar { animation: mc-respirar 10s ease-in-out infinite; }
.mc-entrada[data-ativo="false"] .mc-flutuar,
.mc-entrada[data-ativo="false"] .mc-fluxo,
.mc-entrada[data-ativo="false"] .mc-respirar { animation-play-state: paused; }
@media (prefers-reduced-motion: reduce) {
  .mc-entrada { opacity: 1; transform: none; transition: none; }
  .mc-flutuar, .mc-fluxo, .mc-respirar { animation: none; }
}
`;

// Iluminacao ambiente: maior que o produto, pra vazar pelas bordas dele.
// Verde a esquerda, azul a direita, bem suaves.
function Brilho({ x, y, largura, altura }) {
  return (
    <div
      className="mc-respirar pointer-events-none absolute"
      style={{
        left: x,
        top: y,
        width: largura,
        height: altura,
        background:
          "radial-gradient(ellipse 55% 60% at 35% 50%, rgba(34,197,94,0.20) 0%, transparent 70%), radial-gradient(ellipse 55% 60% at 65% 50%, rgba(59,130,246,0.14) 0%, transparent 70%)",
        filter: "blur(70px)",
      }}
    />
  );
}

// Moldura externa do produto: so acrescenta o glow verde e a sombra profunda
// da composicao — o InboxMockup em si nao muda.
function MolduraProduto({ children }) {
  return (
    <div
      className="rounded-[18px]"
      style={{ boxShadow: "0 0 0 1px rgba(34,197,94,0.10), 0 0 80px -12px rgba(34,197,94,0.22), 0 80px 140px -40px rgba(0,0,0,0.95)" }}
    >
      {children}
    </div>
  );
}

function useVisibilidade(ref) {
  const [entrou, setEntrou] = useState(false);
  const [ativo, setAtivo] = useState(true);

  useEffect(() => {
    const el = ref.current;
    if (!el) return undefined;
    if (typeof IntersectionObserver === "undefined") {
      setEntrou(true);
      return undefined;
    }
    const io = new IntersectionObserver(
      ([entry]) => {
        setAtivo(entry.isIntersecting);
        if (entry.isIntersecting) setEntrou(true);
      },
      { threshold: 0.12 }
    );
    io.observe(el);
    return () => io.disconnect();
  }, [ref]);

  return { entrou, ativo };
}

export default function SecaoMultichannel() {
  const palcoRef = useRef(null);
  const { entrou, ativo } = useVisibilidade(palcoRef);

  return (
    <section
      id="canais"
      data-testid="secao-multichannel"
      className="relative overflow-hidden py-20 md:py-24 px-4 md:px-6"
      style={{ background: "#0A0A0A" }}
    >
      <style>{CSS_SECAO}</style>

      {/* Header — badge, titulo, subtitulo */}
      <div className="relative max-w-5xl mx-auto flex flex-col items-center text-center gap-4 mb-6 md:mb-8">
        <div className="inline-flex items-center gap-2 px-3 py-1 bg-green-500/10 border border-green-500/20 rounded-full">
          <span className="w-1.5 h-1.5 rounded-full bg-green-500" />
          <span className="text-[11px] font-semibold tracking-wider text-green-400 uppercase">Multichannel</span>
        </div>
        <h2 className="text-3xl md:text-4xl lg:text-5xl xl:text-6xl font-extrabold text-white tracking-tight leading-[1.05]">
          Todo o atendimento do seu <br className="hidden md:block" />
          <span className="bg-gradient-to-r from-green-400 to-emerald-300 bg-clip-text text-transparent">negócio</span> em um só lugar
        </h2>
        <p className="text-base md:text-lg text-zinc-400 leading-relaxed max-w-2xl">
          WhatsApp, Instagram, Messenger, Telegram e o chat do seu site numa única caixa de entrada, com VoIP integrado.
          Sua equipe atende tudo do mesmo painel, sem trocar de aba.
        </p>
      </div>

      {/* Produto — palco escalavel */}
      <div
        ref={palcoRef}
        className="mc-entrada relative"
        data-entrou={entrou ? "true" : "false"}
        data-ativo={ativo ? "true" : "false"}
      >
        {/* Desktop (xl+): tela completa com canais orbitando nas laterais */}
        <div className="hidden xl:block mx-auto w-[90%] max-w-[1200px]">
          <PalcoEscalavel largura={PALCO_LARGURA} altura={PALCO_ALTURA}>
            <Brilho x={MOCKUP_X - 140} y={MOCKUP_Y - 90} largura={MOCKUP_LARGURA + 280} altura={MOCKUP_ALTURA + 180} />
            <Conexoes largura={PALCO_LARGURA} altura={PALCO_ALTURA} linhas={LINHAS_DESKTOP} />
            <div className="absolute" style={{ left: MOCKUP_X, top: MOCKUP_Y }}>
              <MolduraProduto>
                <InboxMockup />
              </MolduraProduto>
            </div>
            {NOS_DESKTOP.map((n, i) => (
              <NoCanal key={n.canal} canal={n.canal} x={n.x} y={n.y} tamanho={n.tamanho || TAMANHO_NO} atraso={-i * 1.1} />
            ))}
          </PalcoEscalavel>
        </div>

        {/* Tablet / notebook pequeno (md..xl): canais acima, mockup ocupa a largura toda */}
        <div className="hidden md:block xl:hidden mx-auto w-[95%] max-w-[820px]">
          <PalcoEscalavel largura={TABLET_LARGURA} altura={TABLET_ALTURA}>
            <Brilho x={-60} y={TABLET_MOCKUP_Y - 60} largura={MOCKUP_LARGURA + 120} altura={MOCKUP_ALTURA + 120} />
            <Conexoes largura={TABLET_LARGURA} altura={TABLET_ALTURA} linhas={LINHAS_TABLET} curva="vertical" />
            <div className="absolute" style={{ left: 0, top: TABLET_MOCKUP_Y }}>
              <MolduraProduto>
                <InboxMockup />
              </MolduraProduto>
            </div>
            {NOS_TABLET.map((n, i) => (
              <NoCanal key={n.canal} canal={n.canal} x={n.x} y={n.y} tamanho={TAMANHO_NO_TABLET} atraso={-i * 1.1} />
            ))}
          </PalcoEscalavel>
        </div>

        {/* Mobile: coluna de conversas real num celular + canais convergindo */}
        <div className="md:hidden mx-auto max-w-[400px]">
          <PalcoEscalavel largura={MOBILE_LARGURA} altura={MOBILE_ALTURA}>
            <Brilho x={CELULAR_X - 20} y={CELULAR_Y + 40} largura={CELULAR_LARGURA + 40} altura={CELULAR_ALTURA - 120} />
            <Conexoes largura={MOBILE_LARGURA} altura={MOBILE_ALTURA} linhas={LINHAS_MOBILE} curva="vertical" />
            <div className="absolute" style={{ left: CELULAR_X, top: CELULAR_Y }}>
              <CelularAtendimento />
            </div>
            {NOS_MOBILE.map((n, i) => (
              <NoCanal key={n.canal} canal={n.canal} x={n.x} y={n.y} tamanho={TAMANHO_NO_MOBILE} atraso={-i * 1.1} />
            ))}
          </PalcoEscalavel>
        </div>
      </div>

      {/* 3 cards de beneficios — discretos, nao competem com o mockup */}
      <div
        className="relative mx-auto mt-6 md:mt-8 max-w-[1100px] xl:max-w-[1800px] xl:px-[var(--mc-recuo)]"
        style={{ "--mc-recuo": RECUO_LATERAL }}
      >
        <div className="grid md:grid-cols-3 gap-4 lg:gap-6">
          {FEATURES.map((f) => (
            <div
              key={f.titulo}
              className="p-7 lg:p-8 rounded-3xl border border-white/5 hover:border-white/10 transition-colors duration-300"
              style={{ background: "#111111" }}
            >
              <div className="w-11 h-11 rounded-xl bg-green-500/10 flex items-center justify-center">
                <f.icon className="w-5 h-5 text-green-400" />
              </div>
              <h3 className="mt-5 text-lg lg:text-xl font-semibold text-white">{f.titulo}</h3>
              <p className="mt-2 text-[15px] text-zinc-400 leading-relaxed">{f.descricao}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
